"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TopologicalEditorController = exports.TOPO_TOOL_LOOKUP = void 0;
const tslib_1 = require("tslib");
const core_shared_1 = require("@ikegps/core-shared");
const mobx_1 = require("mobx");
const notification_service_1 = require("../../components/notification.toast/notification.service");
const i18n_1 = require("../../i18n");
const streetview_1 = require("../../modules/streetview");
const bounding_string_1 = require("../../modules/tag.map/bounding.string");
const topological_editor_map_1 = require("../../modules/topological.editor.map");
const point_collection_cache_1 = require("../../modules/topological.editor.map/point.collection.cache");
const topological_editor_tool_1 = require("../../modules/topological.editor.tool");
const services_1 = require("../../services");
const mobx_2 = require("../../state/mobx");
const measurement_state_1 = require("../../state/measurement.state");
const store_1 = require("../../store");
const topological_editor_collection_edit_ctrl_1 = require("./topological.editor.collection.edit.ctrl");
exports.TOPO_TOOL_LOOKUP = {
    relocatePole: topological_editor_tool_1.RelocatePoleToolController,
    createPole: topological_editor_tool_1.CreatePoleToolController,
};
class TopologicalEditorController extends mobx_2.MobxState {
    get isRelocatePoleActive() {
        return this.activeTool instanceof topological_editor_tool_1.RelocatePoleToolController;
    }
    constructor() {
        super();
        this.disposers = [];
        this.mapIsReady = false;
        this.newPoleModel = [];
        this.classnames = [];
        this.measurementState = new measurement_state_1.MeasurementState();
        this.map = new topological_editor_map_1.TopologicalEditorMap({
            onTagClick: (revision) => {
                // Exit create pole mode when clicking on an existing pole
                if (this.isCreatePoleMode) {
                    this.exitTool();
                }
                // Handle measurement mode - set pending measurement collection before selecting
                if (this.measurementState.isActive && !this.measurementState.isPaused) {
                    const collection = this.collectionModels.find((c) => c.revision === revision);
                    if (collection) {
                        this.setPendingMeasurementCollection(collection);
                    }
                }
                this.setSelectedCollectionRevision(revision);
                this.setSelectedVectorId(null);
            },
            onVectorClick: (collectionRevision, vectorId) => {
                // Disable vector selection during active measurement
                if (this.measurementState.isActive && !this.measurementState.isPaused) {
                    return;
                }
                this.setSelectedCollectionRevision(collectionRevision);
                this.setSelectedVectorId(vectorId);
            },
            onVectorDragStart: () => this.exitTool(),
            onMapClick: (position) => this.handleMapClick(position),
            measurementState: this.measurementState,
        });
        this.streetview = new streetview_1.Streetview();
        this.selectedCollectionRevision = null;
        this.selectedCaptureId = null;
        this.selectedVectorId = null;
        this.activeTool = null;
        this.collectionEditControllers = [];
        this.isSaving = false;
        this.isCreatePoleMode = false;
        // Collection awaiting measurement point addition (measurement mode only)
        this.pendingMeasurementCollection = null;
        this.formHasLocationField = false;
        this.mapIsReady = false;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        services_1.Services.Map.isReadyPromise.then(() => {
            services_1.Services.Map.map.setOptions({
                fullscreenControl: false,
            });
            services_1.Services.Map.map.setStreetView(services_1.Services.Streetview.instance);
            services_1.Services.Map.map.getStreetView().setVisible(true);
            this.boundExitTool = this.exitTool.bind(this);
            this.bindReactions();
            this.bindOnCollectionModelsUpdate();
            this.bindOnSelectedCollectionUpdate();
            this.centerMapOnLoad();
            this.bindMapMoveRouteUpdate();
            this.mapIsReady = true;
            this.refreshOverlay();
            this.setSelectedCollectionRevision(null);
            this.formHasLocationField = this.getFormHasLocationField();
        });
    }
    getMapModeMap() {
        var _a;
        return ((_a = this.map) === null || _a === void 0 ? void 0 : _a.map) || null;
    }
    handleMapClick(position) {
        var _a, _b, _c, _d;
        // Handle measurement mode clicks
        if (this.measurementState.isActive && !this.measurementState.isPaused) {
            let location;
            let type;
            let collectionRevision;
            // Check if this click came from a collection marker
            if (this.pendingMeasurementCollection) {
                const collection = this.pendingMeasurementCollection.collection;
                location = new google.maps.LatLng((_b = (_a = collection.location) === null || _a === void 0 ? void 0 : _a.latitude) !== null && _b !== void 0 ? _b : 0, (_d = (_c = collection.location) === null || _c === void 0 ? void 0 : _c.longitude) !== null && _d !== void 0 ? _d : 0);
                type = 'pole';
                collectionRevision = collection.revision;
                this.pendingMeasurementCollection = null; // Clear the flag
            }
            else {
                // Regular map click (not a pole)
                location = position;
                type = 'arbitrary';
                collectionRevision = undefined;
            }
            const point = {
                location,
                type,
                collectionRevision,
                id: crypto.randomUUID(),
            };
            this.measurementState.addPoint(point);
        }
    }
    setPendingMeasurementCollection(collection) {
        this.pendingMeasurementCollection = { collection };
    }
    get tag() {
        if (services_1.Services.Department.current) {
            return store_1.AppStore.Tag.get(services_1.Services.Route.params.get('jobId'));
        }
        return null;
    }
    get routeCollectionUUID() {
        return services_1.Services.Route.params.get('collectionUUID');
    }
    get locationFromRoute() {
        const str = services_1.Services.Route.params.get('location');
        if (str) {
            return bounding_string_1.BoundingString.fromString(str);
        }
        return null;
    }
    get tagSummary() {
        if (this.tag) {
            return store_1.AppStore.TagSummary.get(this.tag.objectId);
        }
        return null;
    }
    get collectionModels() {
        const collections = [];
        // Add saved collections from tag summary
        if (this.tagSummary) {
            collections.push(...this.tagSummary.revisions
                .map((revision) => (store_1.AppStore.Collection.has(revision) ? store_1.AppStore.Collection.get(revision) : null))
                .filter((collection) => collection != null));
        }
        // Add newly created poles that haven't been saved yet
        collections.push(...this.newPoleModel);
        return collections;
    }
    /**
     * Gets the form of the most recently updated collection and checks if it has a location field
     * @returns true if the most recent collection's form has a location field, false otherwise
     */
    getFormHasLocationField() {
        const collections = this.collectionModels;
        if (!collections || collections.length === 0) {
            return false;
        }
        // Sort by updatedAt to find most recent, fallback to createdAt
        const sortedCollections = [...collections].sort((a, b) => {
            const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime; // Most recent first
        });
        const mostRecentCollection = sortedCollections[0];
        if (!mostRecentCollection) {
            return false;
        }
        return mostRecentCollection.editableLocationField != null;
    }
    get mapViewCollections() {
        let collections = [];
        if (this.tagSummary) {
            collections.push(...this.tagSummary.revisions
                .map((revision) => this.getEditCtrlByRevision(revision)
                ? this.getEditCtrlByRevision(revision).collectionEditState.collection
                : store_1.AppStore.Collection.get(revision))
                .filter((collection) => collection != null));
        }
        // Add newly created poles that haven't been saved yet
        collections.push(...this.newPoleModel);
        return collections;
    }
    get selectedCollection() {
        var _a;
        let result = null;
        if (this.selectedCollectionRevision) {
            const hasEdits = this.collectionEditControllers.some((ctrl) => ctrl.revision === this.selectedCollectionRevision);
            if (hasEdits) {
                result =
                    ((_a = this.collectionEditControllers.find((ctrl) => ctrl.revision === this.selectedCollectionRevision)) === null || _a === void 0 ? void 0 : _a.collection) || null;
            }
            else {
                result = store_1.AppStore.Collection.get(this.selectedCollectionRevision);
            }
        }
        return result;
    }
    get selectedCapture() {
        if (this.selectedCollectionRevision) {
            const selectedCollection = store_1.AppStore.Collection.get(this.selectedCollectionRevision);
            return selectedCollection.state.getCapture(this.selectedCaptureId);
        }
        return null;
    }
    getCaptureIds() {
        const selectedCollectionRevision = this.selectedCollectionRevision;
        let captureIds = [];
        if (selectedCollectionRevision) {
            store_1.AppStore.Collection.get(selectedCollectionRevision).forEachField((field) => {
                if (core_shared_1.CollectionModelFieldTrueSize.isTrueSizeField(field)) {
                    const fieldCaptureIds = field.getCaptures().map((capture) => capture.objectId);
                    captureIds = captureIds.concat(fieldCaptureIds);
                }
            });
        }
        return captureIds;
    }
    get selectedCollectionEdit() {
        if (this.selectedCollection) {
            return this.getEditCtrlByRevision(this.selectedCollection.revision);
        }
        return undefined;
    }
    get hasChanges() {
        return this.collectionEditControllers.some((ctrl) => ctrl.collectionEditState.isDirty && ctrl.isValid);
    }
    setSelectedCollectionRevision(revision, captureId) {
        this.selectedCollectionRevision = revision;
        let collection = this.collectionModels.find((cm) => cm.revision === revision);
        if (captureId) {
            this.setSelectedCaptureId(captureId);
        }
        if (this.map && this.map.overlay) {
            this.map.overlay.setSelectedCollectionRevision(revision);
        }
        if (collection) {
            services_1.Services.Route.setParam('collectionUUID', collection.uuid, false);
        }
    }
    setSelectedCaptureId(captureId) {
        this.selectedCaptureId = captureId;
    }
    setSelectedVectorId(id) {
        this.selectedVectorId = id;
    }
    createEditControllerForCollection(collection) {
        if (this.getEditCtrlByRevision(collection.revision)) {
            this.removeEditController(collection.revision);
        }
        this.collectionEditControllers.push(new topological_editor_collection_edit_ctrl_1.TopologicalEditorCollectionEditCtrl(collection, this));
    }
    /**
     * Toggle the create pole mode on/off
     */
    toggleCreatePoleMode() {
        if (this.isCreatePoleMode) {
            this.exitTool();
            this.isCreatePoleMode = false;
        }
        else {
            this.useTool('createPole');
            this.isCreatePoleMode = true;
        }
    }
    zoomToSelectedCollection() {
        if (!this.selectedCollection || !this.selectedCollection.location) {
            return;
        }
        const { latitude, longitude } = this.selectedCollection.location;
        const position = {
            lat: latitude,
            lng: longitude,
        };
        this.map.map.setCenter(position);
        this.map.map.setZoom(20);
    }
    /**
     * Center the map on page load
     */
    centerMapOnLoad() {
        const parsedBounds = this.locationFromRoute;
        if (parsedBounds) {
            this.map.map.setCenter(new google.maps.LatLng(parsedBounds.lat, parsedBounds.lng));
            this.map.map.setZoom(parsedBounds.zoom);
        }
    }
    /**
     * Update the url when the map gets updated after `Services.Analytics.DEBOUNCE_TIME` amount of time
     */
    bindMapMoveRouteUpdate() {
        this.disposers.push((0, mobx_1.reaction)(() => {
            var _a;
            return {
                center: this.map.overlay.bounds && ((_a = this.map.overlay.bounds.getCenter()) === null || _a === void 0 ? void 0 : _a.toJSON()),
                zoom: this.map.overlay.zoom,
            };
        }, (args) => {
            const boundingString = bounding_string_1.BoundingString.toString(args.center, args.zoom);
            if (boundingString === '') {
                return;
            }
            services_1.Services.Route.setParam('location', boundingString);
        }, { delay: services_1.Services.Analytics.DEBOUNCE_TIME }));
    }
    getEditCtrlByRevision(revision) {
        return this.collectionEditControllers.find((x) => x.revision === revision);
    }
    removeEditController(revision) {
        this.collectionEditControllers = this.collectionEditControllers.filter((x) => x.revision !== revision);
    }
    collectionHasChanges(revision) {
        let collectionEditController = this.collectionEditControllers.find((ctrl) => ctrl.revision === revision);
        if (collectionEditController) {
            return collectionEditController.collectionEditState.isDirty && collectionEditController.isValid;
        }
        return false;
    }
    bindOnSelectedCollectionUpdate() {
        this.disposers.push((0, mobx_1.reaction)(() => this.selectedCollection, () => {
            if (this.selectedCollection) {
                if (!this.getEditCtrlByRevision(this.selectedCollection.revision)) {
                    this.createEditControllerForCollection(this.selectedCollection);
                    this.refreshOverlay();
                }
                if (this.selectedCollection.location) {
                    const { latitude, longitude } = this.selectedCollection.location;
                    const collectionPosition = new google.maps.LatLng(latitude, longitude);
                    const updateStreetView = (streetView, panoData) => {
                        const streetViewPosition = panoData.location.latLng;
                        streetView.setPosition(streetViewPosition);
                        streetView.setPano(panoData.location.pano);
                        const heading = google.maps.geometry.spherical.computeHeading(streetViewPosition, collectionPosition);
                        streetView.setPov({
                            heading,
                            pitch: 15,
                        });
                    };
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    services_1.Services.Map.isReadyPromise.then(() => {
                        const streetViewService = services_1.Services.Streetview.streetViewService;
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        streetViewService.getPanorama({ location: collectionPosition, source: google.maps.StreetViewSource.OUTDOOR }, (panoData, status) => {
                            if (status === google.maps.StreetViewStatus.OK) {
                                updateStreetView(services_1.Services.Map.map.getStreetView(), panoData);
                            }
                        });
                    });
                }
            }
        }));
    }
    bindOnCollectionModelsUpdate() {
        this.disposers.push((0, mobx_1.reaction)(() => this.collectionModels, () => {
            this.refreshOverlay();
        }));
    }
    get contentClassNames() {
        return this.classnames.join(' ');
    }
    useTool(toolname) {
        const Tool = exports.TOPO_TOOL_LOOKUP[toolname];
        // If this tool is already active then toggle it off
        if (this.activeTool instanceof Tool) {
            this.exitTool();
        }
        else {
            // Switching to a different tool - exit current and start new
            this.exitTool();
            this.instantiateTool(toolname);
        }
    }
    instantiateTool(toolname) {
        this.eventContext = services_1.Services.Input.getContext();
        if (!exports.TOPO_TOOL_LOOKUP[toolname]) {
            throw new Error(`Could not instantiate Topological Editor tool ${toolname} because it does not exist`);
        }
        this.activeTool = new exports.TOPO_TOOL_LOOKUP[toolname](this);
        this.eventContext.on(this.eventContext.Code.Escape, this.boundExitTool);
        this.map.overlay.setHideTitles(true);
    }
    exitTool() {
        if (this.eventContext) {
            this.eventContext.destroy();
        }
        if (this.activeTool) {
            this.activeTool.destroy();
            this.activeTool = null;
            this.map.overlay.setHideTitles(false);
        }
        // Reset create pole mode when exiting any tool
        this.isCreatePoleMode = false;
    }
    refreshOverlay() {
        if (typeof this.map.overlay !== 'undefined') {
            this.map.overlay.setCollections(this.mapViewCollections);
        }
    }
    destroy() {
        this.exitTool();
        this.map.overlay.onRemove();
        super.destroy();
    }
    batchSave() {
        this.setSaving(true);
        this.exitTool();
        Promise.all(this.collectionEditControllers.map((controller) => controller.batchSaveChanges()))
            .then((result) => {
            this.setSaving(false);
            notification_service_1.NotificationService.success({
                key: (0, i18n_1.Translate)(i18n_1.T.TOPO_SUCCESS_SAVE_JOB, { amount: result.filter(Boolean).length }),
            });
            this.newPoleModel = [];
            this.collectionEditControllers = [];
            this.refreshOverlay();
        })
            .catch((e) => {
            services_1.Services.Log.Logger.error(e, 'TE: Error saving job');
            notification_service_1.NotificationService.error({
                key: i18n_1.T.COLLECTION_EDIT_SAVE_ERROR,
            });
            this.setSaving(false);
            this.newPoleModel = [];
            this.refreshOverlay();
        });
    }
    setSaving(save) {
        this.isSaving = save;
    }
    /**
     * Loops through all available collections and spans and snaps any straggler spans to its nearest collection within a 3m radius
     */
    bulkSnap() {
        const spans = [];
        // Collect all the spans
        this.mapViewCollections.forEach((collection) => {
            // Filter out non spans & spans that are already linked to another collection
            const vectors = point_collection_cache_1.TopologicalPointCollectionCache.getCollectionVectors(collection.revision);
            spans.push(...vectors.filter((x) => x.field.id.includes('spanLength') && !x.field.isCollectionLink()));
        });
        const pendingSnaps = [];
        spans.forEach((span) => {
            const collectionCandidate = { collectionId: null, distance: 3 }; // TODO: PD-966
            // TODO: we can condense this down to an Array.map & Array.sort in the future
            for (const point of this.mapViewCollections) {
                // Skip the collection the span originates from it
                // or if a location is not found
                // of if its a new unsaved pole
                if (point.revision === span.field.collection.revision || !point.location || this.newPoleModel.includes(point)) {
                    continue;
                }
                // Get the distance between the span target and the current point
                const distance = core_shared_1.IkeGeodesic.getDistance(span.target, {
                    latitude: point.location.latitude,
                    longitude: point.location.longitude,
                    altitude: span.target.altitude,
                    /*
                    We use the span target altitude here instead of the point altitude because often the differences in altitude are upwards of 20 meters
                    and if we were to use it, the spans would never snap.

                    Also, fun fact, IkeGeodesic.getDistance never actually uses altitude even though it is a required parameter
                    */
                });
                // If the current point has a closer distance than the active candidate
                if (distance < collectionCandidate.distance) {
                    collectionCandidate.collectionId = point.id;
                    collectionCandidate.distance = distance;
                }
            }
            // Add the span and the to-be linked collection ID
            if (collectionCandidate.collectionId != null) {
                pendingSnaps.push({
                    span,
                    collectionId: collectionCandidate.collectionId,
                });
            }
        });
        pendingSnaps.forEach((data) => {
            // Create an editable state for the source collection
            if (!this.getEditCtrlByRevision(data.span.field.collection.revision)) {
                this.createEditControllerForCollection(data.span.field.collection);
            }
            // Snap the span to the new collection
            data.span.field.Mutator.collection(data.collectionId);
            // Refresh the map drawing for the source collection
            point_collection_cache_1.TopologicalPointCollectionCache.dropCollectionVectors(data.span.field.collection.revision);
        });
    }
    /**
     * Recalculate distance and bearing from non collection linked spans
     */
    updateSpansDistanceBearing(revision) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const vectors = point_collection_cache_1.TopologicalPointCollectionCache.getCollectionVectors(revision);
            const source = point_collection_cache_1.TopologicalPointCollectionCache.getCollectionLocation(revision);
            // update spans distance & bearing that are not collection links
            vectors
                .filter((vector) => !vector.field.isCollectionLink() && core_shared_1.FormModelFieldSubForm.isSpanType(vector.field.parent))
                .map((vector) => {
                const updatedVector = core_shared_1.IkeGeodesic.getVector(source, vector.target);
                vector.field.Mutator.manual(updatedVector);
            });
            point_collection_cache_1.TopologicalPointCollectionCache.dropCollectionVectors(revision);
        });
    }
    bindReactions() {
        this.reactionToSelectedCaptureId();
        this.reactionToSelectedCollectionRevision();
    }
    reactionToSelectedCaptureId() {
        this.reaction(() => this.selectedCaptureId, () => {
            if (services_1.Services.Map.isReady) {
                this.map.overlay.setCapture(this.selectedCapture);
            }
        });
    }
    reactionToSelectedCollectionRevision() {
        this.reaction(() => this.selectedCollectionRevision, () => {
            if (this.selectedCollectionRevision) {
                (0, mobx_1.when)(() => !!store_1.AppStore.Collection.has(this.selectedCollectionRevision), () => {
                    if (services_1.Services.Map.isReady) {
                        const collection = store_1.AppStore.Collection.get(this.selectedCollectionRevision) ||
                            this.newPoleModel.find((pole) => pole.revision === this.selectedCollectionRevision);
                        this.map.overlay.setCapture(collection.state.getCapture(this.getCaptureIds()[0]));
                    }
                });
            }
        });
    }
}
exports.TopologicalEditorController = TopologicalEditorController;
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "newPoleModel", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "classnames", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "selectedCollectionRevision", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "selectedCaptureId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "selectedVectorId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "activeTool", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "collectionEditControllers", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "isSaving", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "isCreatePoleMode", void 0);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "isRelocatePoleActive", null);
tslib_1.__decorate([
    mobx_1.observable
], TopologicalEditorController.prototype, "formHasLocationField", void 0);
tslib_1.__decorate([
    mobx_1.action.bound
], TopologicalEditorController.prototype, "handleMapClick", null);
tslib_1.__decorate([
    mobx_1.action.bound
], TopologicalEditorController.prototype, "setPendingMeasurementCollection", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "tag", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "routeCollectionUUID", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "tagSummary", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "collectionModels", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "mapViewCollections", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "selectedCollection", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "selectedCapture", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "selectedCollectionEdit", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "hasChanges", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "setSelectedCollectionRevision", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "setSelectedCaptureId", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "setSelectedVectorId", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "createEditControllerForCollection", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "toggleCreatePoleMode", null);
tslib_1.__decorate([
    mobx_1.computed
], TopologicalEditorController.prototype, "contentClassNames", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "useTool", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "instantiateTool", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "exitTool", null);
tslib_1.__decorate([
    mobx_1.action
], TopologicalEditorController.prototype, "setSaving", null);
//# sourceMappingURL=topological.editor.ctrl.js.map